import { z } from 'zod'
import type { ApiClient } from './client'
import { ackEnvelope, assertAck } from './envelope'

/**
 * Data model — POST /{clusterId}/api/ddm/list
 *
 * The stream → field catalog the legacy extractor editor shows as its
 * "Data Model Reference". Captured 2026-09-08 (gotham, 139 streams):
 * `data` is a bare array; `stream_id` is `<STREAM>_<scope>`; `fields` are
 * BARE names (no `$`), unlike the search vocabulary (`vfields`), which is
 * `$`-prefixed and covers only streams that currently hold data.
 */

export const ddmStream = z
  .object({
    stream: z.string(),
    stream_id: z.string().optional(),
    fields: z.array(z.string()).default([]),
    scope_id: z.string().optional(),
    created_by: z.string().nullable().optional(),
    updated_by: z.string().nullable().optional(),
    create_tstamp: z.string().nullable().optional(),
    update_tstamp: z.string().nullable().optional(),
  })
  .passthrough()

export const ddmListResponse = ackEnvelope.extend({
  count: z.number().default(0),
  data: z.array(ddmStream).default([]),
})

export type DdmStream = z.output<typeof ddmStream>

/**
 * `POST /{clusterId}/api/ddm` — one endpoint, two intents. With `field` the
 * body is `{scope_id, stream, fields: [field]}` (add a field to a stream);
 * without it `{scope_id, stream}` (declare a new stream). UNTESTED until
 * capture D1 lands (docs/data-model-requirements.md §Capture ledger) —
 * callers render the write disabled outside demo.
 */
export interface DdmAddInput {
  clusterId: string
  scopeId: string
  stream: string
  field?: string
}

export interface DdmApi {
  list: (q: { clusterId: string; scopeId: string }) => Promise<DdmStream[]>
  add: (q: DdmAddInput) => Promise<void>
}

export function createDdmApi(client: ApiClient): DdmApi {
  return {
    list: async ({ clusterId, scopeId }) => {
      const res = await client.request('POST', `/${clusterId}/api/ddm/list`, {
        body: { scope_id: scopeId },
        schema: ddmListResponse,
      })
      assertAck(res, 'Reading the data model')
      return res.data
    },
    add: async ({ clusterId, scopeId, stream, field }) => {
      const body = field === undefined ? { scope_id: scopeId, stream } : { scope_id: scopeId, stream, fields: [field] }
      const res = await client.request('POST', `/${clusterId}/api/ddm`, { body, schema: ackEnvelope })
      assertAck(res, field === undefined ? 'Adding the stream' : 'Adding the field')
    },
  }
}
